import React from 'react';
import { Search, X, ChevronRight, ChevronDown, MapPin, Check, Settings, Bookmark, Clock, Grid3x3, List } from 'lucide-react';
import { getIconComponent } from '../utils/categoryIcons';
import { safeClick } from '../utils/safeClick';
import { formatCategorySlug } from '../utils/formatters';
import { getSubcategoryTranslation, getCategoryTranslation } from '../i18n-subcategories';
import { useLanguage } from '../App';

/**
 * MobileMarketplaceHeader - Compact mobile filter header with:
 * - Search bar
 * - Breadcrumb navigation (Category › Subcategory)
 * - Horizontal scrolling active filter chips
 * - Filters button to open full filter bottom sheet
 */
const MobileMarketplaceHeader = ({
  // Filter state
  filters,
  updateFilter,
  clearFiltersKeepCategory,
  // Categories
  categories,
  activeCategoryInfo,
  // Dropdown setters
  setShowCategoryDropdown,
  setShowSubcategoryDropdown,
  setShowFilters,
  // Search
  searchFocused,
  setSearchFocused,
  recentSearches,
  clearRecentSearches,
  handleSearchSubmit,
  // Island
  currentIsland,
  setCurrentIsland,
  showIslandPicker,
  setShowIslandPicker,
  // Results
  total,
  // Sort
  setShowSortSheet,
  // Quick filter properties
  quickFilterProperties,
  quickFilterSheets,
  setQuickFilterSheets,
  // Active filters count (excluding category/subcategory)
  activeFiltersCount,
  // Price presets getter
  getPricePresets,
  // Save search
  onSaveSearch,
  hasActiveSearch,
  user,
  // View mode
  viewMode,
  setViewMode,
}) => {
  // totalActiveFilters is used for the badge on the Filters button
  const { language, t } = useLanguage();
  
  // Get display names with translations
  const categoryName = activeCategoryInfo?.name 
    ? getCategoryTranslation(activeCategoryInfo.name, language) 
    : formatCategorySlug(filters.category);
  
  // Translate subcategory name
  const subcategoryName = filters.subcategory 
    ? (filters.subcategory.startsWith('all-') 
        ? (language === 'nl' ? `Alle ${categoryName}` : language === 'es' ? `Todo ${categoryName}` : `All ${categoryName}`)
        : getSubcategoryTranslation(filters.subcategory, language))
    : null;
  
  // Count total active filters for badge (excluding category/subcategory)
  let totalActiveFilters = 0;
  if (filters.min_price || filters.max_price) totalActiveFilters++;
  if (filters.listing_type) totalActiveFilters++;
  if (filters.location) totalActiveFilters++;
  Object.entries(filters).forEach(([key, value]) => {
    if (key.startsWith('props_') && value) totalActiveFilters++;
  });

  return (
    <>
      <div 
        className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border"
        data-testid="mobile-marketplace-header"
      >
        <div className="container mx-auto px-4 py-2.5 space-y-2">
          
          {/* Row 1: Search Bar */}
          <div className="relative z-[60]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search listings..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (filters.search.trim()) {
                    handleSearchSubmit(filters.search);
                  }
                  e.target.blur();
                }
              }}
              className="w-full h-9 pl-9 pr-9 text-sm border border-border rounded-xl bg-secondary/30 text-foreground placeholder:text-muted-foreground focus:bg-background focus:border-primary outline-none transition-all"
              data-testid="mobile-search-input"
            />
            {filters.search && (
              <button
                onClick={() => updateFilter('search', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            
            {/* Recent Searches Dropdown */}
            {searchFocused && recentSearches.length > 0 && !filters.search && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-[80] overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
                  <button onClick={clearRecentSearches} className="text-xs text-muted-foreground">Clear</button>
                </div>
                <div className="py-1">
                  {recentSearches.map((term, index) => (
                    <button
                      key={index}
                      onClick={() => { 
                        updateFilter('search', term); 
                        handleSearchSubmit(term);
                        setSearchFocused(false); 
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/50 flex items-center gap-2"
                    >
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="truncate">{term}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Row 2: Breadcrumb Navigation + Type + Filters Button */}
          <div className="flex items-center justify-between gap-2">
            
            {/* Left: Breadcrumb Navigation */}
            <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
              {/* Category Breadcrumb */}
              {filters.category ? (
                <button
                  {...safeClick(() => setShowCategoryDropdown(true))}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium transition-all hover:bg-primary/20 min-w-0"
                  data-testid="mobile-breadcrumb-category"
                >
                  {activeCategoryInfo && (() => {
                    const CatIcon = getIconComponent(activeCategoryInfo.icon);
                    return <CatIcon className="w-3.5 h-3.5 flex-shrink-0" />;
                  })()}
                  <span className="truncate max-w-[80px]">{categoryName}</span>
                </button>
              ) : (
                <button
                  {...safeClick(() => setShowCategoryDropdown(true))}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary text-foreground text-xs font-medium transition-all hover:bg-secondary/80"
                  data-testid="mobile-breadcrumb-all"
                >
                  <span>All Categories</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              )}
              
              {/* Chevron separator and Subcategory */}
              {filters.category && activeCategoryInfo?.subcategories?.length > 0 && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <button
                    {...safeClick(() => setShowSubcategoryDropdown(true))}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all min-w-0 ${
                      filters.subcategory 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary/70 text-foreground hover:bg-secondary'
                    }`}
                    data-testid="mobile-breadcrumb-subcategory"
                  >
                    <span className="truncate max-w-[90px]">
                      {subcategoryName || (language === 'nl' ? 'Selecteer' : language === 'es' ? 'Seleccionar' : 'Select')}
                    </span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  </button>
                </>
              )}
            </div>
            
            {/* Right: Filters Button only */}
            <button
              {...safeClick(() => setShowFilters(true))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0 ${
                totalActiveFilters > 0
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
              }`}
              data-testid="mobile-filters-button"
            >
              <Settings className="w-3.5 h-3.5" />
              <span>{t('filters') || 'Filters'}</span>
              {totalActiveFilters > 0 && (
                <span className="w-5 h-5 bg-white/20 text-[10px] rounded-full flex items-center justify-center font-bold">
                  {totalActiveFilters}
                </span>
              )}
            </button>
          </div>
          
          {/* Row 3: Island Selector + View Mode + Sort */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Island Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowIslandPicker(!showIslandPicker)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                    currentIsland === 'curacao' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                    currentIsland === 'aruba' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                    currentIsland === 'bonaire' ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' :
                    'bg-secondary/50 text-muted-foreground'
                  }`}
                  data-testid="mobile-island-selector"
                >
                  <MapPin className="w-3 h-3" />
                  <span>
                    {currentIsland === 'curacao' ? '🇨🇼' :
                     currentIsland === 'aruba' ? '🇦🇼' :
                     currentIsland === 'bonaire' ? '🇧🇶' :
                     '🌴'}
                  </span>
                  <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showIslandPicker ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Island Picker Dropdown */}
                {showIslandPicker && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowIslandPicker(false)} />
                    <div className="absolute top-full left-0 mt-1.5 w-44 bg-card border border-border rounded-xl shadow-xl z-[60] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="p-1.5">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider px-2 py-1 font-semibold">
                          Select Island
                        </p>
                        {[
                          { id: 'curacao', name: 'Curaçao', flag: '🇨🇼', color: 'bg-blue-500/10' },
                          { id: 'aruba', name: 'Aruba', flag: '🇦🇼', color: 'bg-red-500/10' },
                          { id: 'bonaire', name: 'Bonaire', flag: '🇧🇶', color: 'bg-yellow-500/10' }
                        ].map((island) => (
                          <button
                            key={island.id}
                            onClick={() => {
                              setCurrentIsland(island.id);
                              setShowIslandPicker(false);
                            }}
                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg transition-all text-xs ${
                              currentIsland === island.id
                                ? `${island.color} ring-1 ring-inset ring-primary/20 font-medium`
                                : 'hover:bg-secondary/70 text-foreground'
                            }`}
                            data-testid={`mobile-island-option-${island.id}`}
                          >
                            <span className="text-base">{island.flag}</span>
                            <span className="flex-1 text-left">{island.name}</span>
                            {currentIsland === island.id && (
                              <Check className="w-3.5 h-3.5 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* View Mode + Sort Button */}
            <div className="flex items-center gap-1.5">
              {/* View Mode Switcher - Mobile */}
              <div className="flex items-center bg-secondary/70 rounded-lg p-0.5" data-testid="mobile-view-mode-switcher">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="mobile-view-mode-grid"
                >
                  <Grid3x3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'list' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="mobile-view-mode-list"
                >
                  <List className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Sort Button */}
              <button
                {...safeClick(() => setShowSortSheet(true))}
                data-testid="mobile-sort-button"
                className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium bg-secondary rounded-lg hover:bg-secondary/80 transition-colors touch-manipulation"
              >
                <span>
                  {filters.sort === 'price_low' ? 'Price ↑' : 
                   filters.sort === 'price_high' ? 'Price ↓' : 
                   filters.sort === 'popular' ? 'Popular' : 'Newest'}
                </span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMarketplaceHeader;
